import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../utils/api';
import { REFUND_POLICY_SUMMARY, REFUND_PROCESSING_DAYS } from '../constants/legal';

/**
 * T3 -- what "in flight" means, and why it is copied from the server.
 *
 * Spec: .claude/TODO/duplicate-refund-request-9-24.md, T1/T3.
 *
 * The server's own live predicate (refund_request_service.admin_list, and the
 * T1 submit gate that reuses it) is
 *     status NOT IN ('closed','denied') AND released_refund_id IS NULL
 * and `list_for_user` exposes that second half as the boolean `money_released`.
 * This is the same sentence in JavaScript. If the two ever disagree, the card
 * either hides a form the server would have accepted or offers one the server
 * will refuse -- both of which are the defect this task exists to remove.
 *
 * ⚠ NOTE it is NOT the predicate PaymentsTab uses for its "환불 요청 진행 중"
 * hint, which checks the status alone. That is a decoration; this decides
 * whether a paying customer is shown a form. `approved` with money already
 * released is FINISHED and must not block a later, legitimate partial refund
 * (§4, "what must keep working").
 *
 * A row with no usable status is deliberately NOT treated as live. Unknown must
 * fail OPEN here: the server gate is the authority and a courtesy that silently
 * blocks a paying customer is worse than the bug.
 */
const CLOSED_STATUSES = ['closed', 'denied'];
export const isLiveRequest = (r) =>
  !!r
  && typeof r.status === 'string'
  && !CLOSED_STATUSES.includes(r.status)
  && !r.money_released;

/** Ids are UUIDs on both sides, but one arrives from a quote and one from a
 *  list; compare them as strings and never match two missing values. */
const sameId = (a, b) => !!a && !!b && String(a) === String(b);

/**
 * The live statuses, and only those. `denied` and `closed` are unreachable here
 * by construction (isLiveRequest excludes them) and are left out rather than
 * given wording nothing can render.
 *
 * ⚠ `approved` does NOT read as finished. It is stamped resolved_at on the
 * first terminal transition while the money has not moved (T11), so a bare
 * "승인됨" tells a customer they have been paid when they have not been.
 */
const LIVE_STATUS_COPY = {
  open:      { ko: '접수됨',            en: 'Received' },
  in_review: { ko: '검토 중',           en: 'In review' },
  approved:  { ko: '승인됨 — 지급 대기', en: 'Approved — awaiting payout' },
};

/* Same slice-don't-parse rule as the amount block below and as PaymentsTab:
   these are ISO strings in the server's calendar, and new Date() would move
   a plain 'YYYY-MM-DD' back a day for any viewer west of UTC. */
const ymd = (v) => (v ? String(v).slice(0, 10) : '—');

/**
 * ⚠ INCIDENT O3 -- a missing figure on a billing surface renders as a dash,
 * never as ₩0. The quote columns on a request row are nullable by design
 * (create_request: "the quote travels with the row or not at all"), so a row
 * genuinely can arrive without an amount, and "₩0" is a confident statement
 * that the customer asked for nothing back.
 */
const krwOrDash = (n) =>
  (n === null || n === undefined || n === '' || Number.isNaN(Number(n)))
    ? '—'
    : `₩${Number(n).toLocaleString()}`;

/**
 * The server's sentence, in the reader's language, or nothing -- never
 * "[object Object]" and never a machine code.
 *
 * T2 gives the 409s a bilingual body: `detail` (English, the field every
 * existing client already reads), plus `detail_ko` / `detail_en`. Preferring
 * `detail_ko` on a Korean screen is still the SERVER owning the sentence --
 * it wrote both of them -- it just stops a Korean customer reading an English
 * refusal on an otherwise Korean card.
 *
 * ⚠ `body.error` is NOT a fallback. Since T2 it is a stable machine code
 * ('duplicate_refund_request' / 'refund_not_available') that exists so clients
 * can branch without pattern-matching prose; rendering it would put a symbol
 * where the customer expects a reason.
 *
 * FastAPI sends `detail` as a STRING for our own HTTPExceptions but as a LIST
 * of objects for a request-validation failure, and String() over that renders
 * "[object Object]" -- which swallows the very reason this exists to show.
 */
const serverDetail = (body, isKo) => {
  const localised = isKo ? body?.detail_ko : body?.detail_en;
  if (typeof localised === 'string' && localised.trim()) return localised.trim();
  const d = body?.detail;
  if (typeof d === 'string' && d.trim()) return d.trim();
  if (Array.isArray(d)) {
    const msgs = d.map((x) => (typeof x === 'string' ? x : x?.msg)).filter(Boolean);
    if (msgs.length) return msgs.join(' ');
  }
  return '';
};

/**
 * Self-service refund request — T3.3.
 *
 * Spec: .claude/TODO/Refund-process-refactor-9-2.md — P3 (§10.3, §10.4).
 *
 * WHAT THIS IS, AND WHAT IT DELIBERATELY IS NOT
 * ---------------------------------------------
 * It is a REQUEST form, not a refund button (T3.4, Q2). Confirming files a
 * request with the arithmetic attached; an admin releases it the same day and
 * only then does money move. The customer gets the number instantly, which is
 * the part they actually wanted, and a human stays between the number and the
 * payout.
 *
 * The alternative — self-service execution — would have sat on a path where
 * the cancel call had never once succeeded, nothing was ever revoked, and
 * there was no idempotency. That is a button that pays strangers.
 *
 * WHY THE MONTH COUNT IS THE ONLY THING SENT
 * ------------------------------------------
 * Q4 made the dropdown real: 3 of 8 unused months is a valid request. But the
 * AMOUNT never crosses the wire from here. A client-supplied refund amount is
 * a client-supplied price with the sign flipped; the server re-quotes from the
 * month count and prices it itself.
 *
 * ⚠ THE NON-REFUNDABLE MONTH NEEDS ITS REASON, NOT JUST ITS NUMBER (§10.4).
 * It is the largest single deduction the customer sees and the most likely
 * thing to be disputed. The server sends the reason alongside the figure and
 * this renders both. Do not shorten it to a bare number to tidy the layout.
 *
 * ⚠ IT ALSO SHOWS AN EXISTING REQUEST -- T3, 2026-09-24.
 * -----------------------------------------------------
 * A customer filed a SECOND request against payment 885fe622 while the first
 * sat `approved`. Nothing about that was misuse. The form showed a full,
 * correctly-priced quote and no sign whatever that a request already existed,
 * because `quote_for_user` measures `already_refunded` as a SUM over
 * payment_refunds -- money that ACTUALLY MOVED -- and an approved request
 * awaiting release has moved none. Re-filing is the RATIONAL act of someone
 * who has been told nothing. Two live claims on one payment is how ₩39,800
 * gets paid twice (spec §3).
 *
 * So this card now reads GET /refund-requests too, and renders a live request
 * where the submit form would be. Note what that is and is not: it is a
 * COURTESY. The gate is T1, server-side, in submit_request. If this fetch is
 * slow or fails we show the form anyway and let the server refuse -- a
 * courtesy that silently blocks a paying customer is worse than the bug.
 */
export default function RefundRequestCard({ isKo, onSubmitted, alwaysVisible = false }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  // T3 -- `null` is UNKNOWN (not fetched yet, or the fetch failed) and is NOT
  // "you have no requests". Only an array is an answer. Every read of this
  // value below has to survive `null` by showing the form.
  const [requests, setRequests] = useState(null);

  // The fetch outlives the component in a fast unmount, and the 409 path below
  // fires it again from an event handler where the effect's `cancelled` flag is
  // out of reach. One ref covers both.
  const aliveRef = useRef(true);
  // ⚠ THE SETTER MATTERS AS MUCH AS THE CLEANUP, AND ONLY UNDER StrictMode.
  //
  // This was `useEffect(() => () => { aliveRef.current = false; }, [])` -- a
  // cleanup with nothing that ever sets it back. The app mounts inside
  // <React.StrictMode> (src/index.js:12), and React 18 StrictMode deliberately
  // simulates mount -> unmount -> remount to surface exactly this class of bug.
  // The simulated unmount ran the cleanup while the component was still alive,
  // aliveRef.current stayed false for the rest of its life, and the guard below
  // never fired: `requests` stayed null, so the in-flight panel NEVER rendered
  // in the real app and the customer was shown the submit form again.
  //
  // It passed every test, because tests render the card bare. There is now one
  // that renders it inside StrictMode; do not delete it.
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // The base quote: everything unused. The dropdown then re-quotes per
  // selection rather than dividing locally, so the number on screen is always
  // one the server produced.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/v1/refunds/quote');
        if (res.ok && !cancelled) {
          const d = await res.json();
          setQuote(d);
          if (d.refundable) setMonths(d.months_unused);
        }
      } catch { /* leave the card hidden */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * T3 -- the customer's own requests. Route and shape are NOT invented here:
   * GET /api/v1/refund-requests already exists and settings/PaymentsTab.jsx
   * already consumes it, including the `{items: [...]}` / bare-array tolerance
   * copied below. Self-scoped server-side (`WHERE user_id = :uid`); this sends
   * no ids and can ask for no one else's data.
   *
   * Fields used: `payment_id`, `status`, `money_released`, `submitted_at`,
   * `requested_months`, `quoted_amount_krw`, `quoted_new_end_date` -- all of
   * them selected by list_for_user. `resolution_notes` is deliberately absent
   * from that endpoint (admin free text); do not go looking for it.
   *
   * ⚠ DELIBERATELY NOT AWAITED ALONGSIDE THE QUOTE. Sharing `loading` with the
   * quote would let a slow requests call hold the form off the screen, which is
   * the failure mode this whole task is about, only with a spinner in front of
   * it. The quote decides when the card renders; this decides, whenever it
   * arrives, whether the form is replaced.
   */
  const loadRequests = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/refund-requests');
      if (!res.ok) return;                    // unknown stays unknown
      const d = await res.json();
      const items = Array.isArray(d) ? d : (Array.isArray(d?.items) ? d.items : null);
      if (items && aliveRef.current) setRequests(items);
    } catch {
      /* Unknown stays unknown. Never setRequests([]) here -- an empty array is
         the positive claim "you have no request", and claiming that on a failed
         fetch is how a customer files the second one. */
    }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const reQuote = useCallback(async (m) => {
    setMonths(m);
    try {
      const res = await apiFetch(`/api/v1/refunds/quote?months=${m}`);
      if (res.ok) setQuote(await res.json());
    } catch { /* keep the previous quote rather than blanking the amount */ }
  }, []);

  const submit = useCallback(async () => {
    setError('');
    setSubmitting(true);
    try {
      const res = await apiFetch('/api/v1/refunds/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ months, reason, contact_email: email }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setDone(d);
        setOpen(false);
        if (onSubmitted) onSubmitted(d);
      } else {
        // T3(c) -- THE SERVER OWNS THIS SENTENCE. A 409 from the T1 gate names
        // the existing request (its submitted date and status); a 409 from
        // RefundNotAvailable says there is nothing left to refund. Both are
        // rendered verbatim. Writing our own copy for "409" would have to pick
        // one of those two meanings and would be wrong half the time, on a
        // billing surface -- the O3 rule applies to sentences as much as to
        // figures. The local string below is a fallback for an empty body only.
        //
        // 429 is untouched and still means the rate limit
        // (RATE_LIMIT_MAX_REQUESTS in refund_request_service.py). T2 moves the
        // DUPLICATE refusal off 429 and onto 409 precisely so the customer stops
        // being told to wait when the useful advice is "look at your request".
        setError(serverDetail(d, isKo) || (isKo ? '요청을 접수하지 못했습니다.' : 'Could not submit the request.'));
        if (res.status === 409) {
          // The server has just told us the state we were missing. Re-read it so
          // the card can replace the form with the real request rather than
          // leaving the customer looking at a form that cannot succeed.
          //
          // On EVERY 409, not only on `error === 'duplicate_refund_request'`.
          // The code exists and is the right thing to branch on where the two
          // cases need different screens, but this is one idempotent GET: the
          // cost of re-reading after a `refund_not_available` is a request that
          // finds nothing and changes nothing, and gating on the code would
          // make an older or unrecognised 409 body silently skip the refresh.
          loadRequests();
        }
      }
    } catch {
      setError(isKo ? '네트워크 오류가 발생했습니다.' : 'A network error occurred.');
    }
    setSubmitting(false);
  }, [months, reason, email, isKo, onSubmitted, loadRequests]);

  // C3 - `alwaysVisible` keeps a refund entry point on screen for PLAN
  // SUBSCRIBERS even when there is nothing quotable right now. Without it this
  // component renders NOTHING in three separate states (loading, not
  // refundable with no reason, and not refundable at all), so a paying customer
  // could open the 구독 tab and find no evidence that refunds exist. The quote
  // logic below is unchanged and still decides what can actually be requested -
  // this only governs whether the customer can SEE the path.
  if (loading) {
    return alwaysVisible
      ? <p className="mt-4 text-xs" style={{ color: 'var(--text-secondary)' }}>
          {isKo ? '환불 가능 여부를 확인하는 중…' : 'Checking refund eligibility…'}
        </p>
      : null;
  }
  if (done) {
    return (
      <div className="mt-4 p-3 rounded" style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.4)' }}>
        <p style={{ fontSize: 14, fontWeight: 600 }}>
          {isKo ? '환불 요청이 접수되었습니다.' : 'Your refund request has been received.'}
        </p>
        <p className="mt-1" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {isKo
            ? `영업일 기준 1일 이내에 검토 후 연락드리며, 승인되면 최초 결제 수단으로 영업일 ${REFUND_PROCESSING_DAYS}일 이내에 환불됩니다.`
            : `We review requests within 1 business day. Once approved, the refund is returned to your original payment method within ${REFUND_PROCESSING_DAYS} business days.`}
        </p>
      </div>
    );
  }

  /**
   * T3 -- is there already a live request for THE PAYMENT THIS CARD WOULD REFUND?
   *
   * Not "for this customer": the spec requires that a live request on one
   * payment never blocks a legitimate request on a DIFFERENT one, so the
   * payment id from the quote is the join key.
   *
   * DERIVED BEFORE THE not-refundable EARLY RETURN, and that ordering is the
   * point. It used to sit after it, so the one customer who most needs to see
   * their pending request -- the one whose quote has since gone
   * non-refundable, because an admin already shortened the term -- was the one
   * customer who could not. They got a bare "nothing is refundable", which
   * reads as "your request vanished".
   *
   * `_not_refundable` omits payment_id, so in that state there is no join key
   * and we fall back to ANY live request. That is sound here: we are no longer
   * deciding whether to allow a submit (the server owns that, T1) -- only
   * whether to tell the customer something is in progress.
   *
   * `requests === null` is the UNKNOWN case and yields null, which is the whole
   * point: a failed or still-pending fetch shows the form and lets the server
   * refuse. A courtesy that silently blocks a paying customer is worse than the
   * bug it was added to fix.
   */
  const liveRequests = Array.isArray(requests) ? requests.filter(isLiveRequest) : [];
  const inFlight = quote?.payment_id
    ? (liveRequests.find((r) => sameId(r.payment_id, quote.payment_id)) || null)
    : (liveRequests[0] || null);

  // Nothing refundable is a normal state, not an error. Say why, quietly --
  // UNLESS a request is already in flight, in which case the panel below is the
  // more honest answer and this branch must not swallow it.
  if (!quote?.refundable && !inFlight) {
    const why = quote ? (isKo ? quote.reason_ko : quote.reason_en) : null;
    if (!why) {
      // Never leave a plan subscriber with no explanation at all.
      return alwaysVisible
        ? <p className="mt-4 text-xs" style={{ color: 'var(--text-secondary)' }}>
            {isKo
              ? '현재 환불 가능한 결제 내역이 없습니다. 문의: hello@whereat.kr'
              : 'There is no refundable payment on this account right now. Contact hello@whereat.kr'}
          </p>
        : null;
    }
    return (
      <p className="mt-4 text-xs" style={{ color: 'var(--text-secondary)' }}>
        {isKo ? '환불: ' : 'Refunds: '}{why}
      </p>
    );
  }

  const krw = (n) => `₩${(n || 0).toLocaleString()}`;
  // `quote` may now be non-refundable (and months_unused undefined) when an
  // in-flight request carried us past the branch above. Array.from over an
  // undefined length yields [], and the in-flight panel returns before this
  // list is ever rendered -- but optional chaining is what keeps that true if
  // the order changes again.
  const monthOptions = Array.from({ length: quote?.months_unused || 0 }, (_, i) => i + 1);

  if (inFlight) {
    const statusCopy = LIVE_STATUS_COPY[inFlight.status];
    // An unrecognised status is shown as the server's own word rather than
    // guessed at. The vocabulary is pinned by a CHECK constraint, but a
    // historical row can sit outside it and this is a billing surface.
    const statusLabel = statusCopy
      ? (isKo ? statusCopy.ko : statusCopy.en)
      : String(inFlight.status);
    const whatNext = inFlight.status === 'approved'
      ? (isKo
          ? `승인되었습니다. 최초 결제 수단으로 영업일 ${REFUND_PROCESSING_DAYS}일 이내에 환불됩니다.`
          : `Approved. The refund is returned to your original payment method within ${REFUND_PROCESSING_DAYS} business days.`)
      : statusCopy
        ? (isKo
            ? `영업일 기준 1일 이내에 검토 후 연락드리며, 승인되면 최초 결제 수단으로 영업일 ${REFUND_PROCESSING_DAYS}일 이내에 환불됩니다.`
            : `We review requests within 1 business day. Once approved, the refund is returned to your original payment method within ${REFUND_PROCESSING_DAYS} business days.`)
        : null;

    return (
      <div className="mt-5 pt-4" style={{ borderTop: '1px solid var(--border-color)' }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
          {isKo ? '환불 요청' : 'Request a refund'}
        </h4>

        {/* The plan facts stay on screen. They describe the TERM, not the
            pending claim, so they remain true while a request is open. */}
        <div className="text-sm space-y-1" style={{ color: 'var(--text-secondary)' }}>
          <div className="flex justify-between">
            <span>{isKo ? '남은 기간' : 'Time remaining'}</span>
            <span>{isKo ? `${quote.days_remaining}일 (미사용 ${quote.months_unused}개월)`
                       : `${quote.days_remaining} days (${quote.months_unused} unused months)`}</span>
          </div>
          <div className="flex justify-between">
            <span>{isKo ? '월 환산 요금' : 'Monthly rate'}</span>
            <span>{krw(quote.monthly_rate_krw)}</span>
          </div>
        </div>

        {/* ⚠ THE SPECULATIVE "환불 예정 금액" BOX IS DELIBERATELY NOT RENDERED
            HERE. It prices every unused month, i.e. a request the customer has
            NOT made, and would sit beside the smaller figure they actually did
            ask for. Two refund amounts on one card, one of them fictional, is
            the O3 failure in its most expensive form. The customer sees what
            they asked for -- below, from their own request row. */}
        <div
          className="mt-3 p-3 rounded"
          style={{ background: 'rgba(234,179,8,0.10)', border: '1px solid rgba(234,179,8,0.45)' }}
        >
          <div className="flex justify-between items-baseline gap-3">
            <strong style={{ fontSize: 14 }}>
              {isKo ? '이미 접수된 환불 요청이 있습니다.' : 'You already have a refund request in progress.'}
            </strong>
            <span style={{ fontSize: 12, whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
              {statusLabel}
            </span>
          </div>

          <div className="mt-2" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            <div className="flex justify-between py-0.5">
              <span>{isKo ? '접수일' : 'Submitted'}</span>
              <span>{ymd(inFlight.submitted_at)}</span>
            </div>
            {inFlight.requested_months != null && (
              <div className="flex justify-between py-0.5">
                <span>{isKo ? '요청 개월 수' : 'Months requested'}</span>
                <span>{isKo ? `${inFlight.requested_months}개월` : `${inFlight.requested_months} months`}</span>
              </div>
            )}
            {/* "요청 금액", never "환불 예정 금액": `approved` is not a promise of
                payment, and the row can legitimately carry no quote at all. */}
            <div className="flex justify-between py-0.5">
              <span>{isKo ? '요청 금액' : 'Requested amount'}</span>
              <span>{krwOrDash(inFlight.quoted_amount_krw)}</span>
            </div>
            {inFlight.quoted_new_end_date && (
              <div className="flex justify-between py-0.5">
                <span>{isKo ? '환불 후 이용 종료일' : 'Term would end'}</span>
                <span>{ymd(inFlight.quoted_new_end_date)}</span>
              </div>
            )}
          </div>

          {whatNext && (
            <p className="mt-2" style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              {whatNext}
            </p>
          )}
          <p className="mt-1" style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {isKo
              ? '진행 상황은 설정 › 결제에서 확인하실 수 있습니다. 변경이 필요하시면 hello@whereat.kr로 문의해 주세요.'
              : 'You can follow its progress under Settings › Payments. To change it, email hello@whereat.kr.'}
          </p>

          {/* The 409 the customer just received, if they got here by submitting.
              The server's sentence names the existing request, so it is kept on
              screen rather than replaced by the panel that refreshed under it. */}
          {error && <p className="mt-2" style={{ fontSize: 13, color: '#d33' }}>{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 pt-4" style={{ borderTop: '1px solid var(--border-color)' }}>
      <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
        {isKo ? '환불 요청' : 'Request a refund'}
      </h4>

      <div className="text-sm space-y-1" style={{ color: 'var(--text-secondary)' }}>
        <div className="flex justify-between">
          <span>{isKo ? '남은 기간' : 'Time remaining'}</span>
          <span>{isKo ? `${quote.days_remaining}일 (미사용 ${quote.months_unused}개월)`
                     : `${quote.days_remaining} days (${quote.months_unused} unused months)`}</span>
        </div>
        <div className="flex justify-between">
          <span>{isKo ? '월 환산 요금' : 'Monthly rate'}</span>
          <span>{krw(quote.monthly_rate_krw)}</span>
        </div>
      </div>

      {/* Q4 — a real selection, not a display. Each change re-quotes server-side. */}
      <label className="block mt-3" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
        {isKo ? '환불할 개월 수' : 'Months to refund'}
        <select
          value={months ?? quote.months_unused}
          onChange={(e) => reQuote(Number(e.target.value))}
          className="block mt-1 w-full"
          style={{
            padding: '8px 10px', borderRadius: 6, fontSize: 14,
            border: '1px solid var(--border-color)',
            background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          }}
        >
          {monthOptions.map(m => (
            <option key={m} value={m}>
              {isKo ? `${m}개월 — ${krw(m * quote.monthly_rate_krw)}` : `${m} months — ${krw(m * quote.monthly_rate_krw)}`}
            </option>
          ))}
        </select>
      </label>

      <div
        className="mt-3 p-3 rounded"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
      >
        <div className="flex justify-between items-baseline">
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {isKo ? '환불 예정 금액' : 'Refund amount'}
          </span>
          <strong style={{ fontSize: 20, color: '#a100aa' }}>{krw(quote.refundable_krw)}</strong>
        </div>

        {/* §10.4 — the reason, not only the deduction. */}
        {quote.non_refundable_krw > 0 && (
          <p className="mt-2" style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {isKo ? '환불되지 않는 금액 ' : 'Not refunded: '}
            <strong>{krw(quote.non_refundable_krw)}</strong>
            {' — '}
            {isKo ? quote.non_refundable_reason_ko : quote.non_refundable_reason_en}
          </p>
        )}

        {/* ⚠ NOT new Date(...).toLocaleDateString(). new_end_date is a plain
            'YYYY-MM-DD', which Date parses as UTC MIDNIGHT — so any viewer west of
            UTC sees the day BEFORE. Reported live: a term ending 2026-09-10
            rendered as 9/9/2026. Slice it; it is already the date the server
            means, in the server's own calendar.
            Wording follows the `shortened` clause in constants/legal.js:
            analysis reports are PROVIDED for a term, never credits granted
            (KG이니시스 review, 2026-10-02). */}
        {quote.new_end_date && (
          <p className="mt-2" style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {isKo
              ? `환불이 완료되면 이용 기간이 ${String(quote.new_end_date).slice(0, 10)}까지로 단축되며, 단축된 기간에는 분석 리포트가 제공되지 않습니다.`
              : `Once released, your term is shortened to ${String(quote.new_end_date).slice(0, 10)}, and no analysis reports are provided for the refunded months.`}
          </p>
        )}
      </div>

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-3"
          style={{
            padding: '10px 18px', borderRadius: 6, fontSize: 14, fontWeight: 600,
            cursor: 'pointer', background: 'transparent',
            color: 'var(--text-primary)', border: '1px solid var(--border-color)',
          }}
        >
          {isKo ? '환불 요청하기' : 'Request this refund'}
        </button>
      ) : (
        <div className="mt-3 space-y-2">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={isKo ? '환불 사유를 알려주세요 (10자 이상).' : 'Tell us why (at least 10 characters).'}
            style={{
              width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 14,
              border: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)', color: 'var(--text-primary)',
            }}
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={isKo ? '연락받을 이메일' : 'Contact email'}
            style={{
              width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 14,
              border: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)', color: 'var(--text-primary)',
            }}
          />

          {/* Explicit confirmation of what is being agreed to, restating the
              amount rather than referring to it. The customer should not have to
              scroll back up to know what they are about to accept. */}
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {isKo
              ? `${months}개월분 ${krw(quote.refundable_krw)} 환불을 요청합니다. ${REFUND_POLICY_SUMMARY.ko}`
              : `You are requesting a refund of ${krw(quote.refundable_krw)} for ${months} months. ${REFUND_POLICY_SUMMARY.en}`}
          </p>

          {error && <p style={{ fontSize: 13, color: '#d33' }}>{error}</p>}

          <div className="flex gap-2">
            <button
              type="button"
              disabled={submitting || reason.trim().length < 10 || !email.trim()}
              onClick={submit}
              style={{
                padding: '10px 18px', borderRadius: 6, fontSize: 14, fontWeight: 600,
                border: 'none', color: '#FFFFFF',
                cursor: submitting ? 'wait' : 'pointer',
                opacity: (reason.trim().length < 10 || !email.trim()) ? 0.5 : 1,
                background: 'linear-gradient(135deg, #931DA4 0%, #123293 100%)',
              }}
            >
              {submitting
                ? (isKo ? '접수 중…' : 'Submitting…')
                : (isKo ? '환불 요청 확인' : 'Confirm refund request')}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setError(''); }}
              style={{
                padding: '10px 18px', borderRadius: 6, fontSize: 14, fontWeight: 600,
                cursor: 'pointer', background: 'transparent',
                color: 'var(--text-primary)', border: '1px solid var(--border-color)',
              }}
            >
              {isKo ? '취소' : 'Cancel'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
